import React, { useContext } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { BrandContext } from "./BrandContext.js";
import { VenueBrandForContext } from "./venueBrand.js";
import type {
  Item,
  ItemMod,
  ItemModGroup,
  PriceUnit,
  PricingType,
} from "./types";
//* Rounds up to 2 dp but matches the input value
//eg
//  1 => 1
//  1.2 => 1.2
//  1.23 => 1.23
//  1.234 => 1.23
const terseRounding = (value: number) => parseFloat(value.toFixed(2));
const fixedRounding = (value: number) =>
  parseFloat(value.toString()).toFixed(2);

const isMandatoryGroup = ({ modGroupType }: ItemModGroup) =>
  modGroupType === "mandatory";
const isDefaultMod = (i: ItemMod) => i.favourite;

const itemDefaultPrice = (itemModGroups: ItemModGroup[]) =>
  itemModGroups
    .filter(isMandatoryGroup)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .flatMap(({ itemMods }) => itemMods.filter(isDefaultMod))
    .map(({ salesPrice }) => salesPrice)
    .reduce((a, b) => +a + +b, 0);

const priceUnitsPerSymbols = {
  item: "/item",
  kilogram: "/Kg",
  "100 gram": "/100g",
  gram: "/g",
  litre: "/L",
  millilitre: "/mL",
  open: "",
};

export const Price = ({
  priceUnit,
  value,
}: {
  priceUnit?: PriceUnit;
  value: number;
}) => {
  const { t } = useTranslation();
  const { currencyDisplayType } = useContext(
    BrandContext
  ) as VenueBrandForContext;
  const isTerse =
    currencyDisplayType == "terse_with_symbol" ||
    currencyDisplayType == "terse";
  const hasSymbol =
    currencyDisplayType == "terse_with_symbol" ||
    currencyDisplayType == "normal_with_symbol";

  const hasPriceUnit = priceUnit && priceUnit != "item";
  const usedValue = isTerse ? terseRounding(+value) : fixedRounding(+value);

  return (
    <>
      {`${hasSymbol ? t("currencySymbol") : ""}${usedValue}${
        hasPriceUnit ? priceUnitsPerSymbols[priceUnit] : ""
      }
  `}
    </>
  );
};

const SimplePrice = ({ item }: { item: Item }) => {
  return <Price value={+item.salesPrice} priceUnit={item.priceUnit} />;
};

const DefaultPrice = ({ item }: { item: Item }) => {
  return (
    <Price
      value={+item.salesPrice + itemDefaultPrice(item.itemModGroups)}
      priceUnit={item.priceUnit}
    />
  );
};

const ModsPrice = ({ item }: { item: Item }) => {
  const [firstModGroup, ...remainingModGroups] =
    item.itemModGroups.filter(isMandatoryGroup);
  if (!firstModGroup) {
    return (
      <div className="text-right">
        <DefaultPrice item={item} />
      </div>
    );
  }

  return (
    <tr>
      <td colSpan={2} className="pb-8 pt-2 flex justify-end text-right">
        {firstModGroup.itemMods.slice(0, 3).map((mod) => (
          <div
            key={mod.name}
            className="border-r border-neutral-700 last:border-r-0 last:pr-0 px-4 "
          >
            <Price
              value={
                +item.salesPrice +
                itemDefaultPrice(remainingModGroups) +
                +mod.salesPrice
              }
            />
          </div>
        ))}
      </td>
    </tr>
  );
};
const Item = ({
  item,
  pricingType,
  itemDescription,
}: {
  item: Item;
  pricingType: PricingType;
  itemDescription: string;
}) => {
  const { t } = useTranslation();

  const hasItemDescription = item.detail;
  const hasModPrices = pricingType == "mods_list";

  return (
    <React.Fragment>
      <tr className="align-top">
        <td className="w-full">
          <div>{item.name} </div>
        </td>

        {!hasModPrices && (
          <td className={"text-right"}>
            {pricingType == "simple" && <SimplePrice item={item} />}
            {pricingType == "default_mods" && <DefaultPrice item={item} />}
          </td>
        )}
      </tr>
      {hasItemDescription && (
        <tr>
          <td
            colSpan={2}
            className={classNames({
              "p-0 h-0": !hasItemDescription,
              "pb-8": !hasModPrices,
            })}
          >
            <div className="text-xs italic"> {item.detail} </div>
          </td>
        </tr>
      )}
      {hasModPrices && <ModsPrice item={item} />}
    </React.Fragment>
  );
};

export default Item;
